import { useState } from "react";
import { useOnMount } from "../../hooks/useOnMount.ts";
import { useAppMutation } from "../../http/useAppMutation.ts";
import type { CharacterModel, ObjectModel } from "../types.ts";
import { ModelNameForm } from "./CreationPage/ModelNameForm.tsx";
import { ModelTrainingImagesForm } from "./CreationPage/ModelTrainingImagesForm.tsx";
import { ObjectAndCharacterDescriptionForm } from "./CreationPage/ObjectAndCharacterDescriptionForm.tsx";
import { TrainingModelLoadingScreen } from "./CreationPage/TrainingModelLoadingScreen.tsx";
import { ModelDescription } from "./ModelDescription.tsx";
import { ModelTrainDialog } from "./ModelTrainDialog.tsx";
import type { ObjectAndCharacterModelCreationStep } from "./types.ts";

export const ObjectAndCharacterModelCreation = ({
  model,
}: {
  model: ObjectModel | CharacterModel;
}) => {
  const [currentModelCreationStep, setCurrentModelCreationStep] =
    useState<ObjectAndCharacterModelCreationStep>("modelTrainingImagesForm");
  const [
    isTrainingModelPurchaseDialogOpen,
    setIsTrainingModelPurchaseDialogOpen,
  ] = useState(false);

  const generateElementDescription = useAppMutation({
    path: "references/generate-element-description",
    invalidate: [],
  }).mutation;

  useOnMount(() => {
    //  XXX: If we start again the onboarding we set the step to the latest step that was reached
    if (model.status === "READY") {
      setCurrentModelCreationStep("modelDescription");
    } else if (model.status === "TRAINING") {
      setCurrentModelCreationStep("trainingModelLoadingScreen");
    } else if (model.status === "CREATED") {
      if (
        model.element_description?.length === 0 ||
        model.element_description === null
      ) {
        setCurrentModelCreationStep("modelTrainingImagesForm");
      } else if (model.name === null) {
        setCurrentModelCreationStep("modelNameForm");
      } else {
        setCurrentModelCreationStep("modelNameForm");
      }
    }
  });

  switch (currentModelCreationStep) {
    case "modelTrainingImagesForm":
      return (
        <ModelTrainingImagesForm
          onClickNext={() => {
            setCurrentModelCreationStep("ObjectAndCharacterDescriptionForm");
            generateElementDescription.mutate({ reference_uuid: model.uuid });
          }}
        />
      );
    case "ObjectAndCharacterDescriptionForm":
      // XXX: We inject model to benefit from the type narrowing of the parent component instead of relying on the hook
      // `Model` that would return a generic Model type
      return (
        <ObjectAndCharacterDescriptionForm
          model={model}
          onClickNext={() => {
            setCurrentModelCreationStep("modelNameForm");
          }}
          onClickPrevious={() => {
            setCurrentModelCreationStep("modelTrainingImagesForm");
          }}
        />
      );
    case "modelNameForm":
      return (
        <>
          <ModelNameForm
            onClickPrevious={() =>
              setCurrentModelCreationStep("ObjectAndCharacterDescriptionForm")
            }
            onClickNext={() => {
              setIsTrainingModelPurchaseDialogOpen(true);
            }}
          />
          <ModelTrainDialog
            isOpen={isTrainingModelPurchaseDialogOpen}
            onOpenChange={setIsTrainingModelPurchaseDialogOpen}
            modelUuid={model.uuid}
            trainAction="train"
            onTrainModelSuccess={() =>
              setCurrentModelCreationStep("trainingModelLoadingScreen")
            }
          />
        </>
      );
    case "trainingModelLoadingScreen":
      return <TrainingModelLoadingScreen />;
    case "modelDescription":
      return <ModelDescription />;
  }
};
